import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CheckCircle, Clock, AlertTriangle, Wifi, WifiOff, UserCheck } from 'lucide-react';

interface SurgeonRow {
  id: string;
  name: string;
  specialty: string;
  onboardingStage: 'data_ingestion' | 'ai_processing' | 'profile_ready' | 'live';
  onboardingProgress: number;
  ehrStatus: 'connected' | 'pending' | 'error';
  readyForLive: boolean;
  casesProcessed: number;
}

const mockSurgeons: SurgeonRow[] = [
  { id: '1', name: 'Dr. Peter Larkin', specialty: 'Orthopedics', onboardingStage: 'live', onboardingProgress: 100, ehrStatus: 'connected', readyForLive: true, casesProcessed: 127 },
  { id: '2', name: 'Dr. Sarah Chen', specialty: 'Orthopedics', onboardingStage: 'profile_ready', onboardingProgress: 90, ehrStatus: 'connected', readyForLive: false, casesProcessed: 0 },
  { id: '3', name: 'Dr. Michael Torres', specialty: 'Sports Medicine', onboardingStage: 'ai_processing', onboardingProgress: 55, ehrStatus: 'pending', readyForLive: false, casesProcessed: 0 },
  { id: '4', name: 'Dr. Amanda Wright', specialty: 'Orthopedics', onboardingStage: 'data_ingestion', onboardingProgress: 20, ehrStatus: 'error', readyForLive: false, casesProcessed: 0 },
  { id: '5', name: 'Dr. James Okafor', specialty: 'Hand Surgery', onboardingStage: 'profile_ready', onboardingProgress: 85, ehrStatus: 'connected', readyForLive: false, casesProcessed: 0 },
];

const stageLabels: Record<SurgeonRow['onboardingStage'], { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  data_ingestion: { label: 'Data Ingestion', variant: 'outline' },
  ai_processing: { label: 'AI Processing', variant: 'secondary' },
  profile_ready: { label: 'Profile Ready', variant: 'default' },
  live: { label: 'Live', variant: 'default' },
};

function EhrBadge({ status }: { status: SurgeonRow['ehrStatus'] }) {
  if (status === 'connected') return <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 gap-1"><Wifi className="w-3 h-3" />Connected</Badge>;
  if (status === 'pending') return <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30 gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
  return <Badge variant="destructive" className="gap-1"><WifiOff className="w-3 h-3" />Error</Badge>;
}

export function SurgeonStatusTracker() {
  const live = mockSurgeons.filter(s => s.readyForLive).length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-primary" />
            Surgeon Onboarding Status
          </CardTitle>
          <div className="flex gap-3 text-sm text-muted-foreground">
            <span>{mockSurgeons.length} surgeons</span>
            <span className="text-emerald-600 font-medium">{live} live</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Surgeon</TableHead>
              <TableHead>Onboarding Stage</TableHead>
              <TableHead className="hidden md:table-cell">Progress</TableHead>
              <TableHead>EHR Integration</TableHead>
              <TableHead className="text-center">Ready for Live</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mockSurgeons.map(s => {
              const stage = stageLabels[s.onboardingStage];
              return (
                <TableRow key={s.id}>
                  <TableCell>
                    <div>
                      <span className="font-medium">{s.name}</span>
                      <p className="text-xs text-muted-foreground">{s.specialty}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={stage.variant} className={s.onboardingStage === 'live' ? 'bg-emerald-600 text-white' : ''}>
                      {stage.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Progress value={s.onboardingProgress} className="h-2 flex-1" />
                      <span className="text-xs text-muted-foreground w-8">{s.onboardingProgress}%</span>
                    </div>
                  </TableCell>
                  <TableCell><EhrBadge status={s.ehrStatus} /></TableCell>
                  <TableCell className="text-center">
                    {s.readyForLive ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
